import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthSession, LoginBody } from '@controlstock/contracts';
import { apiClient, ApiRequestError, clearCsrfToken, setCsrfToken } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';

/**
 * Resolves to `null` when no valid server-side session exists. Other transport
 * and server failures remain query errors so protected routes can distinguish a
 * sign-in redirect from an outage.
 */
export function useSession() {
  return useQuery<AuthSession | null, ApiRequestError>({
    queryKey: queryKeys.auth.session(),
    queryFn: async () => {
      try {
        const session = await apiClient.auth.getSession();
        setCsrfToken(session.csrfToken);
        return session;
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) {
          clearCsrfToken();
          return null;
        }
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation<AuthSession, ApiRequestError, LoginBody>({
    mutationFn: apiClient.auth.login,
    onSuccess: async (session) => {
      setCsrfToken(session.csrfToken);
      queryClient.removeQueries({ queryKey: queryKeys.root });
      queryClient.setQueryData(queryKeys.auth.session(), session);
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all() });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiRequestError, void>({
    mutationFn: apiClient.auth.logout,
    onSuccess: () => {
      clearCsrfToken();
      queryClient.removeQueries({ queryKey: queryKeys.root });
    },
  });
}
